import {
  get,
  put,
  post,
  patch,
  deleteApi,
  checkHttpStatus,
} from "./httpFunctions";
import { AxiosResponse } from "axios";
import { intl } from "../containers/app";
import { handleError } from "./errorLogger";
import { error, success } from "./messages";

/**
 * odešle HTTP PATCH na API
 * @param entity název entity
 * @param entityId ID entity
 * @param properties vlastnosti entity k úpravě
 * @param what český název v 1. pádě
 * @param whatError český název ve 4. pádě
 * @param setLoading funkce pro nastavení načítání
 * @param loadEntities funkce pro načtení seznamu entit
 * @param she byla nebo byl smazána
 */
export function patchEntity(
  entity: string,
  entityId: number,
  properties: any,
  what?: string,
  whatError?: string,
  setLoading?: any,
  loadEntities?: any,
  she: boolean = false
) {
  setLoading && setLoading(true);
  const query = `${entity}/${entityId}`;
  patch(query, createPatchModel(properties))
    .then((r) => {
      if (checkHttpStatus(r)) {
        success(
          r.data.message ||
            intl.formatMessage(
              {
                id: "crud.edit",
                defaultMessage: "{what} byl{suffix} úspěšně upraven{suffix}.",
              },
              { what, suffix: she ? "a" : "" }
            ),
          10
        );
        loadEntities && loadEntities();
      } else {
        error(
          (r.data && r.data.message) ||
            intl.formatMessage(
              {
                id: "crud.editError",
                defaultMessage: "Při úpravě {whatError} nastala chyba!",
              },
              { whatError }
            ),
          10
        );
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setLoading && setLoading(false);
      whatError &&
        error(
          intl.formatMessage(
            {
              id: "crud.editError",
              defaultMessage: "Při úpravě {whatError} nastala chyba!",
            },
            { whatError }
          ),
          10
        );
      handleError(err);
    });
}

/**
 * odešle HTTP DELETE na API
 * @param entity název entity
 * @param entityId ID entity
 * @param what český název v 1. pádě
 * @param whatError český název ve 4. pádě
 * @param loadEntities funkce pro načtení seznamu entit
 * @param onError funkce pro vzhodnoceni chyby
 * @param she byla nebo byl smazána
 * @param message hláška k zobrazení
 * @param errorMessage chybová hláška k zobrazení
 */
export function deleteEntity(
  entity: string,
  entityId: number,
  what: string,
  whatError: string,
  loadEntities: any,
  onError: any,
  she: boolean = false,
  message: string = "",
  errorMessage: string = ""
) {
  const query = `${entity}/${entityId}`;
  deleteApi(query)
    .then((r) => {
      if (checkHttpStatus(r)) {
        success(
          message ||
            intl.formatMessage(
              {
                id: "crud.delete",
                defaultMessage: "{what} byl{suffix} úspěšně smazán{suffix}.",
              },
              { what, suffix: she ? "a" : "" }
            )
        );
        loadEntities && loadEntities();
      } else {
        error(
          errorMessage ||
            (r.data && r.data.message) ||
            intl.formatMessage(
              {
                id: "crud.deleteError",
                defaultMessage: "Při mazání {whatError} nastala chyba!",
              },
              { whatError }
            ),
          10
        );
        onError && onError();
      }
    })
    .catch((err) => {
      error(
        intl.formatMessage(
          {
            id: "crud.deleteError",
            defaultMessage: "Při mazání {whatError} nastala chyba!",
          },
          { whatError }
        ),
        10
      );
      handleError(err);
      onError && onError();
    });
}

/**
 * odešle HTTP POST na API
 * @param entity název entity
 * @param setLoading funkce nastavení načítání
 * @param data původní data entity
 * @param what český název v 1. pádě
 * @param whatError český název ve 4. pádě
 * @param she byla nebo byl smazána
 * @param onResult fce, vraci pri ok data, pri chybe nic
 * @param message hláška k zobrazení
 * @param errorMessage chybová hláška k zobrazení
 * @param json jestli maji byt request data json
 */
export function postEntity(
  entity: string,
  setLoading: any,
  data: any,
  what: string,
  whatError: string,
  she: string,
  onResult: any,
  message: string = "",
  errorMessage: string = "",
  json: boolean = true
) {
  setLoading && setLoading(true);
  post(entity, data, true, json)
    .then((r) => {
      if (checkHttpStatus(r)) {
        onResult && onResult(r.data, 200);
        success(
          message ||
            intl.formatMessage(
              {
                id: "crud.create",
                defaultMessage: "{what} byl{suffix} úspěšně vytvořen{suffix}.",
              },
              { what, suffix: she ? "a" : "" }
            )
        );
      } else {
        error(
          errorMessage ||
            (r.data && r.data.message) ||
            intl.formatMessage(
              {
                id: "crud.createError",
                defaultMessage: "Při vytváření {whatError} nastala chyba!",
              },
              { whatError }
            ),
          10
        );
        onResult && onResult(undefined, r.status);
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setLoading && setLoading(false);
      onResult && onResult();
      handleError(err);
    });
}

/**
 * odešle HTTP PUT na API
 * @param entity název entity
 * @param entityId ID entity
 * @param setLoading funkce nastavení načítání
 * @param data původní data entity
 * @param newData nová data entity
 * @param what český název v 1. pádě
 * @param whatError český název ve 4. pádě
 * @param she byla nebo byl smazána
 * @param onResult fce, vraci pri ok data, pri chybe nic
 * @param message hláška k zobrazení
 * @param errorMessage chybová hláška k zobrazení
 * @param json jestli maji byt request data json
 */
export function putEntity(
  entity: string,
  entityId: number,
  setLoading: any,
  data: any,
  newData: any,
  what: string,
  whatError: string,
  she: string,
  onResult: any,
  message: string = "",
  errorMessage: string = "",
  json: boolean = true
) {
  setLoading(true);
  const query = `${entity}/${entityId}`;
  put(query, { ...data, ...newData }, true, json)
    .then((r) => {
      if (checkHttpStatus(r)) {
        onResult && onResult(r.data);
        success(
          message ||
            intl.formatMessage(
              {
                id: "crud.edit",
                defaultMessage: "{what} byl{suffix} úspěšně upraven{suffix}.",
              },
              { what, suffix: she ? "a" : "" }
            )
        );
      } else {
        error(
          errorMessage ||
            (r.data && r.data.message) ||
            intl.formatMessage(
              {
                id: "crud.editError",
                defaultMessage: "Při úpravě {whatError} nastala chyba!",
              },
              { whatError }
            ),
          10
        );
        onResult && onResult(undefined, r.status);
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setLoading && setLoading(false);
      onResult && onResult();
      handleError(err);
    });
}

/**
 * načte entitu pomocí HTTP GET
 * @param entity název entity
 * @param entityId ID entity
 * @param setLoading funkce pro nastavení načítání
 * @param setEntity funkce pro nastavení entity
 * @param showMessage ukazat error message
 * @param parameters parametry pro query string
 */
export function getEntity(
  entity: string,
  entityId: number,
  setLoading: any,
  setEntity: any,
  parameters?: any,
  showMessage: boolean = false,
  isBlob: boolean = false
) {
  setLoading && setLoading(true);
  const query = `${entity}${entityId ? `/${entityId}` : ""}${createQueryString(
    parameters
  )}`;
  get(query, true, isBlob)
    .then((r) => {
      if (checkHttpStatus(r)) {
        setEntity(r.data);
      } else {
        showMessage && showMessageIfExists(r);
        setEntity("e", r.status);
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setEntity("e");
      setLoading && setLoading(false);
      handleError(err);
    });
}

/**
 * načte seznam entit pomocí HTTP GET
 * @param entity název entity
 * @param setEntities funkce pro nastavení entit
 * @param setLoading funkce pro nastavení načítání
 * @param mapFunction mapovací funkce - nepovinná, pokud není nechá data v originále
 * @param parameters parametry pro query string
 * @param cleanData jestli ma dojit k promazani dat pomoci setEntities
 */
export function getEntities(
  entity: string,
  setEntities: any,
  setLoading: any,
  mapFunction?: any,
  parameters?: any,
  cleanData: boolean = true
) {
  cleanData && setEntities();
  setLoading && setLoading(true);
  const query = entity + createQueryString(parameters);
  get(query)
    .then((r) => {
      if (checkHttpStatus(r)) {
        setEntities(
          mapFunction
            ? r.data.map(mapFunction)
            : r.data.map((d: any, i: number) => ({ ...d, key: i }))
        );
      } else {
        showMessageIfExists(r);
        setEntities("e");
      }
      setLoading && setLoading(false);
    })
    .catch((err) => {
      setEntities("e");
      handleError(err);
      setLoading && setLoading(false);
    });
}

/**
 * ukáže odpověď API, pokud existuje
 * @param response odpověď od axios
 */
export function showMessageIfExists(response: AxiosResponse) {
  response &&
    response.data &&
    response.data.message &&
    error(response.data.message, 10);
}

/**
 * vytvoří query string z parametrů
 * @param parameters parametry
 */
export function createQueryString(parameters?: any): string {
  return parameters
    ? `?${Object.keys(parameters)
        .filter(
          (key) => parameters[key] !== undefined && parameters[key] !== null
        )
        .map((key) => `${key}=${encodeURIComponent(parameters[key])}`)
        .join("&")}`
    : "";
}

/**
 * vytvoří model pro PATCH
 * @param properties parametry pro PATCH
 */
function createPatchModel(properties: any): any {
  return (
    properties &&
    Object.keys(properties).map((key) => ({
      propertyName: key,
      value: properties[key],
    }))
  );
}
